import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SessionProposedActionDataStatus } from '@vendasta/protos/ai_assistants/v1alpha1';
import { getSessionEvent$ } from '@vendasta/protos/ai_assistants/v1alpha1/session-service';
import { catchError, combineLatest, map, of, switchMap } from 'rxjs';
import { ProposedActionApproval, ProposedActionRejection } from './ai-proposed-action.types';
import { NamespaceFromTypeAndId } from '../../core/services/ai-assistant-utils';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';

@Component({
  selector: 'ai-proposed-action',
  imports: [
    MatButtonModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    AiAssistantI18nModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ai-proposed-action.component.html',
  styleUrls: ['./ai-proposed-action.component.scss'],
})
export class AiProposedActionComponent {
  readonly namespaceType = input.required<string>();
  readonly namespaceId = input.required<string>();
  readonly sessionId = input.required<string>();
  readonly eventId = input.required<string>();
  readonly superseded = input<boolean>(false);

  readonly approved = output<ProposedActionApproval>();
  readonly rejected = output<ProposedActionRejection>();

  protected readonly isRejecting = signal(false);
  protected readonly feedbackText = signal('');
  protected readonly actionInProgress = signal(false);

  private readonly snackbarService = inject(SnackbarService);
  private readonly refreshTrigger = signal(0);

  protected readonly data = toSignal(
    combineLatest([
      toObservable(this.namespaceId),
      toObservable(this.namespaceType),
      toObservable(this.sessionId),
      toObservable(this.eventId),
      toObservable(this.refreshTrigger),
    ]).pipe(
      switchMap(([namespaceId, namespaceType, sessionId, eventId]) => {
        const namespace = NamespaceFromTypeAndId(namespaceType, namespaceId);
        return getSessionEvent$({ namespace, sessionId, eventId }).pipe(
          map((response) => response.event?.proposedAction ?? null),
          catchError(() => {
            this.snackbarService.openErrorSnack('AI_ASSISTANT.PROPOSED_ACTION.FETCH_ERROR');
            return of(null);
          }),
        );
      }),
    ),
    { initialValue: null },
  );

  protected readonly isPending = computed(
    () => this.data()?.status === SessionProposedActionDataStatus.PENDING && !this.superseded(),
  );
  protected readonly isApproved = computed(() => this.data()?.status === SessionProposedActionDataStatus.APPROVED);
  protected readonly isRejected = computed(() => this.data()?.status === SessionProposedActionDataStatus.REJECTED);
  protected readonly isSuperseded = computed(
    () =>
      this.data()?.status === SessionProposedActionDataStatus.SUPERSEDED ||
      (this.data()?.status === SessionProposedActionDataStatus.PENDING && this.superseded()),
  );

  protected readonly formattedArguments = computed(() => {
    const args = this.data()?.arguments;
    if (!args) return '';
    try {
      return JSON.stringify(JSON.parse(args), null, 2);
    } catch {
      return args;
    }
  });

  reload(): void {
    this.refreshTrigger.update((n) => n + 1);
  }

  resetActionInProgress(): void {
    this.actionInProgress.set(false);
  }

  protected onApprove(): void {
    this.actionInProgress.set(true);
    this.approved.emit({
      sessionId: this.sessionId(),
      eventId: this.eventId(),
    });
  }

  protected onStartReject(): void {
    this.isRejecting.set(true);
  }

  protected onCancelReject(): void {
    this.isRejecting.set(false);
    this.feedbackText.set('');
  }

  protected onSubmitRejection(): void {
    this.actionInProgress.set(true);
    this.rejected.emit({
      sessionId: this.sessionId(),
      eventId: this.eventId(),
      feedback: this.feedbackText(),
    });
  }

  protected onFeedbackInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.feedbackText.set(target.value);
  }
}
